import {
    BooleanNumber,
    DisplayUrl,
    EditField,
    FileResult,
    InputFields,
    InputFieldsReturn,
    InputOptions,
    InputProcData,
    MessageProcData,
    ProcBaseData,
    ProcData
} from 'priority-web-sdk';
import {
    showConfirmMessage,
    showErrorMessage,
    showInfoMessage
} from '../../_shared/Messages/actions/MessageActions';
import messagesText from '../../_shared/Messages/texts';
import {getLocalizedText, texts} from '../../constants/TextConst';
import {startProcedure} from './service';
import {setIsNavigationEnabled, startEntity} from '../../_shared/GWTBridge/invoke/genPage';

export type LinkData = {
    type: string;
    ename: string;
};

export const runProcedure = async (
    procName: string,
    updateInputFields = defaultUpdateFunction,
    selectOptions = null,
    createDataFile = null,
    dname: string | null = null
): Promise<any> => {
    setIsNavigationEnabled(false);
    try {
        const procRes: ProcData = await startProcedure(procName, 'P', dname);
        await processProcStepResult(procRes, updateInputFields, selectOptions, createDataFile);
    } catch (e) {
        console.log('An error occured while trying to run procedure', e);
    } finally {
        setIsNavigationEnabled(true);
    }
};

const handleDeliveryOrderForReport = async (procRes: ProcData) => {
    return new Promise(async (resolve, reject) => {
        const proc = procRes.proc;
        switch (procRes.type) {
            case 'message':
                const msgRes = procRes as ProcBaseData & MessageProcData;
                if (msgRes.messagetype === 'information') {
                    showInfoMessage({content: msgRes.message});
                } else if (msgRes.messagetype === 'warning') {
                    const res = await proc.continueProc();
                    resolve(handleDeliveryOrderForReport(res));
                } else if (msgRes.messagetype === 'error') {
                    showErrorMessage({title: msgRes.message, onConfirm: () => reject()});
                }
                break;
            case 'reportOptions':
                const reportOptionsRes = await proc.reportOptions(1, -1);
                resolve(handleDeliveryOrderForReport(reportOptionsRes));
                break;
            case 'displayUrl':
                const {Urls} = procRes as DisplayUrl;
                if (Urls?.[0].action === 'display' && Urls[0].url.length) {
                    const newWin = window.open(encodeURI(Urls[0].url), '_blank');
                    if (!newWin || newWin.closed || typeof newWin.closed === 'undefined') {
                        throw new Error('Popup blocked');
                    }
                    resolve(Urls[0].url);
                }
                break;
            default:
                break;
        }
    });
};

export const onHyperlinkClick = async (linkData: LinkData) => {
    const {type, ename} = linkData;
    switch (type) {
        case 'R':
        case 'P':
            try {
                const proc = await startProcedure(ename, type);
                await handleDeliveryOrderForReport(proc);
            } catch (e) {
                console.log('An error occured while trying to handle hyperlink click', e);
            }
            break;
        case 'F':
            try {
                await startEntity(ename);
            } catch (e) {
                console.log('An error occured while trying to handle hyperlink click', e);
            }
            break;
        default:
            return;
    }
};

export const processProcStepResultMessageHandler = (
    resolve,
    reject,
    processProcStepResult,
    procRes: ProcData,
    updateFunc,
    selectOptions,
    createDataFile = null
) => {
    const proc = procRes.proc;
    const msgRes = procRes as ProcBaseData & MessageProcData;
    if (msgRes.messagetype === 'information') {
        const linkData = msgRes.hyperlinks?.map(link => {
            return {
                ...link,
                linkData: {
                    type: link.type,
                    ename: link.ename
                }
            };
        });
        const processProcContinue = async () => {
            const res = await proc.message(1);
            resolve(processProcStepResult(res, updateFunc, selectOptions, createDataFile));
        };
        showInfoMessage({
            content: msgRes.message,
            onLinkClick: onHyperlinkClick,
            linkData,
            onClose: processProcContinue
        });
    } else if (msgRes.messagetype === 'warning') {
        const processProcContinue = async (userResponse: BooleanNumber) => {
            const res = await proc.message(userResponse);
            resolve(processProcStepResult(res, updateFunc, selectOptions, createDataFile));
        };
        showConfirmMessage({
            content: msgRes.message,
            onConfirm: () => processProcContinue(1),
            onCancel: () => processProcContinue(0),
            confirmButtonText: messagesText.yes,
            cancleButtonText: messagesText.cancel,
            defaultButton: 0
        });
    } else if (msgRes.messagetype === 'error') {
        showErrorMessage({
            title: msgRes.message,
            onConfirm: () => reject(),
            onClose: () => reject(),
            confirmButtonText: getLocalizedText(texts.confirmLabel)
        });
    }
};

export const processProcStepResult = async (
    procRes: ProcData,
    updateFunc,
    selectOptions,
    createDataFile = null
) => {
    return new Promise(async (resolve, reject) => {
        try {
            const proc = procRes.proc;
            switch (procRes.type) {
                case 'upload':
                    const mimeType = 'text/plain';
                    const dataUrl = createDataFile();
                    const uploadRes: FileResult = await proc.uploadDataUrl(
                        dataUrl,
                        mimeType,
                        () => {}
                    );
                    resolve(
                        processProcStepResult(
                            uploadRes as unknown as ProcData,
                            updateFunc,
                            selectOptions,
                            createDataFile
                        )
                    );
                    break;
                case 'inputFields':
                    const input: InputFields = (procRes as InputProcData).input as InputFields;
                    const inputFields: EditField[] = input.EditFields;
                    const updatedInputFields: InputFieldsReturn = updateFunc(inputFields);
                    const res = await proc.inputFields(1, updatedInputFields);
                    resolve(processProcStepResult(res, updateFunc, selectOptions, createDataFile));
                    break;
                case 'inputOptions':
                    const options = (procRes as InputProcData).input as InputOptions;
                    const resOptions = await proc.inputOptions(1, selectOptions?.(options));
                    resolve(
                        processProcStepResult(resOptions, updateFunc, selectOptions, createDataFile)
                    );
                    break;
                case 'message':
                    processProcStepResultMessageHandler(
                        resolve,
                        reject,
                        processProcStepResult,
                        procRes,
                        updateFunc,
                        selectOptions,
                        createDataFile
                    );
                    break;
                case 'reportOptions':
                    const reportOptionsRes = await proc.reportOptions(1, -1);
                    resolve(
                        processProcStepResult(
                            reportOptionsRes,
                            updateFunc,
                            selectOptions,
                            createDataFile
                        )
                    );
                    break;
                case 'displayUrl':
                    const displayUrlRes = procRes as ProcBaseData & DisplayUrl;
                    const url = displayUrlRes.Urls?.[0].url;
                    window.open(url, '_blank');
                    resolve(url);
                    break;
                case 'end':
                    resolve('');
                    break;
                default:
                    break;
            }
        } catch (e) {
            console.log('An error occured while trying to process procedure step result', e);
            reject();
        }
    });
};

// as a default option we send back to the server the values that we got from the server
export const defaultUpdateFunction = (inputFields: EditField[]): InputFieldsReturn => {
    const inputFieldsReturn = inputFields.map(field => {
        const value = field.value;

        return {
            field: field.field,
            op: 0,
            value: value,
            op2: 0,
            value2: value
        };
    });
    return {EditFields: inputFieldsReturn};
};

export const buildDataUrlFromParams = (params: string[]) => {
    const mimeType = 'text/plain';
    const base64Content = btoa(params.join('\n'));
    const dataUrl = `data:${mimeType};base64,${base64Content}`;
    return dataUrl;
};
